import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography, useTheme, Fade, useMediaQuery, CircularProgress } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import MessageInput from './MessageInput'
import MessagesList from './MessagesList'
import CapabilityChips from './CapabilityChips'
import { RateLimitGate } from '../../../../components/FeatureGate'
import { UsageIndicator } from '../../../../components/UsageIndicator'
import { FEATURES } from '../../../../contexts/SubscriptionProvider'
import { EventSourcePolyfill } from 'event-source-polyfill'
import { backendUrl } from '../../../../config'
import {
  fetchConversation,
  fetchConversations,
  setMessages,
  setMessageLoading,
  setStreamingMessageId,
  updateMessageSources,
  updateMessageTools,
  addToolExecution,
  setMessageError,
  resetConversation,
  updateMessageNodeExecution,
  updateMessagePlan,
  updateMessageStepExecution,
  updateMessageMetadata,
  updateMessageArtifacts,
  updateConversationTitleInStore,
  clearPendingFiles,
  setDocumentProcessingStatus,
  clearDocumentProcessingStatus,
} from '../../../../redux/actions/conversationAction'
import { setSearchMode } from '../../../../redux/actions/userAction'

/**
 * Chat Component with ReAct Engine Integration
 *
 * Features:
 * - Real-time streaming responses
 * - Tool call visualization
 * - Task progress tracking
 * - Document references
 */
const Chat = ({ onPreviewChange, previewContent: externalPreviewContent }) => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language
  const { conversationId } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  // Redux state
  const user = useSelector(state => state.auth.user?.user)
  const userId = user?.id
  const authToken = useSelector(state => state.auth.user?.token)
  const messages = useSelector(state => state.conversations.messages)
  const isLoading = useSelector(state => state.conversations.loadingMessage)
  const isFetching = useSelector(state => state.conversations.loading)
  const pendingFiles = useSelector(state => state.conversations.pendingFiles) || []
  const countryPref = useSelector(state => state.user?.countryPreference)
  // Normalize to array
  const countries = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA']
  const jurisdictionPref = useSelector(state => state.user?.jurisdictionPreference) || {}
  const mode = useSelector(state => state.user?.searchMode) || 'law'
  // Note: Language is now auto-detected by the backend, no need to send it

  // Local state (only form input)
  const [userMessage, setUserMessage] = useState('')

  // Preview state lifted to ConversationLayout
  const previewContent = externalPreviewContent
  const handleOpenPreview = (content, title, type) => {
    // Save scroll position before panel layout changes
    if (messagesScrollRef.current) {
      savedScrollRef.current = messagesScrollRef.current.scrollTop
    }
    if (onPreviewChange) onPreviewChange(content, title, type)
  }

  // Save scroll when document viewer opens (triggered externally via Redux)
  const documentData = useSelector(state => state.document?.document)
  const documentSourceUrl = useSelector(state => state.document?.documentSourceUrl)
  useEffect(() => {
    if ((documentData || documentSourceUrl) && messagesScrollRef.current) {
      savedScrollRef.current = messagesScrollRef.current.scrollTop
    }
  }, [documentData, documentSourceUrl])

  // Restore scroll position after any panel layout change
  useEffect(() => {
    if (savedScrollRef.current !== null && messagesScrollRef.current) {
      const saved = savedScrollRef.current
      // Use double rAF to wait for layout recalculation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (messagesScrollRef.current) {
            messagesScrollRef.current.scrollTop = saved
          }
          savedScrollRef.current = null
        })
      })
    }
  }, [previewContent, documentData, documentSourceUrl])

  // Derived state
  const showEmptyState = messages.length === 0

  // Refs
  const textFocusRef = useRef()
  const fileInputRef = useRef()
  const messagesEndRef = useRef()
  const messagesScrollRef = useRef(null)
  const savedScrollRef = useRef(null)
  const eventSourceRef = useRef(null)
  const messagesRef = useRef(messages)  // Keep latest messages for event handlers

  // Responsive
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // Keep messagesRef updated with latest messages for event handlers
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // Reset mode to 'law' if only BF is selected and current mode is not 'law'
  useEffect(() => {
    const onlyBF = countries.length === 1 && countries[0] === 'BF'
    if (onlyBF && mode !== 'law') {
      dispatch(setSearchMode('law'))
    }
  }, [countries, mode, dispatch])

  // Track previous conversationId to detect actual changes
  const prevConversationIdRef = useRef(conversationId)

  // Fetch conversation only when conversationId actually changes
  useEffect(() => {
    const prevId = prevConversationIdRef.current
    prevConversationIdRef.current = conversationId

    // Only clear and refetch if the conversationId actually changed
    if (prevId !== conversationId) {
      // Stop any ongoing stream from the previous conversation
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      dispatch(setMessageLoading(false))
      dispatch(setStreamingMessageId(null))
      dispatch(clearPendingFiles())
      dispatch(clearDocumentProcessingStatus())

      dispatch(setMessages([]))

      if (conversationId) {
        dispatch(fetchConversation(conversationId))
      }
    }

    // Cleanup EventSource on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [conversationId, dispatch])

  // Scroll to bottom when messages change
  const streamingMessageId = useSelector(state => state.conversations.streamingMessageId)
  const prevStreamingIdRef = useRef(streamingMessageId)
  useEffect(() => {
    if (messagesEndRef.current && !isFetching && savedScrollRef.current === null) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isFetching])

  // Extra scroll after streaming ends — ensures action bar is visible
  useEffect(() => {
    if (prevStreamingIdRef.current && !streamingMessageId && messagesEndRef.current) {
      // Streaming just ended — scroll after React renders the action bar
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      })
    }
    prevStreamingIdRef.current = streamingMessageId
  }, [streamingMessageId])

  const handleTextChange = (event) => {
    setUserMessage(event.target.value.replace(/<br\s*\/?>/gi, '\n'))
  }

  const handleSubmit = async () => {
    if (!userMessage.trim() || isLoading) return

    const message = userMessage.trim()
    const isFirstMessage = messages.length === 0
    setUserMessage('')
    dispatch(setMessageLoading(true))

    // Add user message to UI (include attached file names so they persist visually)
    const userMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: message,
      created_at: new Date().toISOString(),
      ...(pendingFiles.length > 0 && {
        attached_documents: pendingFiles.map(f => ({ filename: f.name })),
      }),
    }

    // Add pending assistant message with tool execution state
    const assistantMsgId = `assistant-${Date.now()}`
    const assistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      sources: [],
      tool_executions: [],
      is_streaming: true,
      created_at: new Date().toISOString(),
      activeTools: [],
      completedTools: [],
      workflowProgress: 0,
      currentThought: '',
      node_executions: [],
      execution_plan: null,
      metadata: null,
    }

    // Dispatch both messages to Redux
    dispatch(setMessages([...messages, userMsg, assistantMsg]))
    dispatch(setStreamingMessageId(assistantMsgId))

    // Local accumulators for streaming (to avoid too many dispatches)
    let accumulatedContent = ''
    let accumulatedArtifacts = []
    let nodeExecutions = []
    let executionPlan = null

    // Create or use conversation
    // Note: The SSE endpoint will auto-create the conversation if it doesn't exist
    let convId = conversationId
    if (!convId) {
      // Generate a new conversation ID client-side
      convId = crypto.randomUUID()
      navigate(`/chat/${convId}`, { replace: true })
    }

    // Upload pending files if any
    let uploadedDocIds = []
    if (pendingFiles.length > 0) {
      try {
        // Show uploading status immediately so user sees progress
        const uploadingStatus = pendingFiles.map((file, idx) => ({
          id: `uploading-${idx}`,
          filename: file.name,
          status: 'uploading',
        }))
        dispatch(setDocumentProcessingStatus(uploadingStatus))

        const formData = new FormData()
        pendingFiles.forEach(file => formData.append('documents', file))
        formData.append('organization_name', user?.organization_name || 'default')

        const uploadRes = await fetch(`${backendUrl}api/conversation/${convId}/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken?.access_token}` },
          body: formData,
        })

        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}))
          throw new Error(errData.detail || 'Upload failed')
        }

        const uploadResult = await uploadRes.json()
        uploadedDocIds = uploadResult.map(f => f.file_id)
        dispatch(clearPendingFiles())

        // Enrich user message with upload metadata (url, file_type) for preview
        const enrichedDocs = uploadResult.map(f => ({
          filename: f.original_file_name || f.file_name,
          url: f.url,
          file_type: f.file_type,
          file_id: f.file_id,
        }))
        userMsg.attached_documents = enrichedDocs
        dispatch(setMessages([...messages, { ...userMsg, attached_documents: enrichedDocs }, assistantMsg]))

        // Poll document processing status until all ready (max 120s)
        const pollInterval = 2000
        const maxAttempts = 60 // 120s / 2s
        for (let i = 0; i < maxAttempts; i++) {
          const statusRes = await fetch(`${backendUrl}api/conversation/${convId}/documents/status`, {
            headers: { Authorization: `Bearer ${authToken?.access_token}` },
          })
          if (!statusRes.ok) break

          const docs = await statusRes.json()
          dispatch(setDocumentProcessingStatus(docs))

          const allReady = docs.every(d => d.status === 'ready')
          const anyError = docs.some(d => d.status === 'error')

          if (allReady) break
          if (anyError) {
            const errorDoc = docs.find(d => d.status === 'error')
            throw new Error(`Failed to process: ${errorDoc.filename}`)
          }

          await new Promise(resolve => setTimeout(resolve, pollInterval))
        }

        dispatch(clearDocumentProcessingStatus())
      } catch (err) {
        console.error('File upload error:', err)
        enqueueSnackbar(err.message || t('error_occurred'), { variant: 'error' })
        dispatch(setMessageLoading(false))
        dispatch(setStreamingMessageId(null))
        dispatch(clearDocumentProcessingStatus())
        return
      }
    }

    // Build jurisdiction codes from user preferences
    const chatJurisdictionCodes = []
    for (const c of countries) {
      const pref = jurisdictionPref[c]
      if (pref !== undefined && pref !== null && Array.isArray(pref)) {
        chatJurisdictionCodes.push(...pref)
      }
    }

    // Build query params for new agentic engine
    // Note: user_id is passed in query params because EventSource doesn't support custom headers
    const countriesParam = countries.join(',') // Comma-separated for multiple countries
    const docTypeMap = { law: 'law', case_law: 'case-law', regulation: 'regulation' }
    const docTypes = mode.split(',').map(m => docTypeMap[m.trim()]).filter(Boolean).join(',') || 'law'

    const params = new URLSearchParams({
      user_message: message,
      user_id: userId,
      countries: countriesParam, // Comma-separated for multiple countries
      doc_types: docTypes,  // Comma-separated: "law", "case-law", or "law,case-law"
      ui_language: currentLang.split('-')[0], // Pass UI language for reasoning display
    })

    // Pass uploaded document IDs if any
    if (uploadedDocIds.length > 0) {
      params.set('user_document_ids', uploadedDocIds.join(','))
    }

    // Pass jurisdiction codes if user has specific sub-jurisdiction selection
    if (chatJurisdictionCodes.length > 0) {
      params.set('jurisdiction_codes', chatJurisdictionCodes.join(','))
    }

    // Connect to SSE endpoint with auth header
    const eventSource = new EventSourcePolyfill(
      `${backendUrl}api/chat/conversations/${convId}/message/stream?${params}`,
      { headers: { Authorization: `Bearer ${authToken?.access_token}` } }
    )
    eventSourceRef.current = eventSource

    // Helper to update message in Redux
    const updateMessage = (updates) => {
      dispatch({
        type: 'SYSTEM_SEND_MESSAGE',
        payload: { id: assistantMsgId, ...updates },
      })
    }

    // New agentic engine events

    // Node execution events
    eventSource.addEventListener('node_start', (event) => {
      const data = JSON.parse(event.data)
      console.log('Node started:', data.node_name, data.description)

      nodeExecutions.push({
        node_name: data.node_name,
        status: 'started',
        description: data.description,
        started_at: data.timestamp,
        completed_at: null,
      })

      dispatch(updateMessageNodeExecution(assistantMsgId, [...nodeExecutions]))
    })

    eventSource.addEventListener('node_complete', (event) => {
      const data = JSON.parse(event.data)
      console.log('Node completed:', data.node_name, data.reasoning ? `reasoning: ${data.reasoning}` : '')

      nodeExecutions = nodeExecutions.map(node =>
        node.node_name === data.node_name && node.status === 'started'
          ? {
              ...node,
              status: 'completed',
              completed_at: data.timestamp,
              reasoning: data.reasoning || null,
              result_metadata: data.metadata || null,
            }
          : node
      )

      dispatch(updateMessageNodeExecution(assistantMsgId, [...nodeExecutions]))
    })

    // Plan events
    eventSource.addEventListener('plan_created', (event) => {
      const data = JSON.parse(event.data)
      console.log('Plan created:', data.steps)

      executionPlan = {
        steps: data.steps.map(step => ({
          ...step,
          status: 'pending',
          started_at: null,
          completed_at: null,
        })),
        strategy: data.strategy,
        created_at: data.timestamp,
      }

      dispatch(updateMessagePlan(assistantMsgId, executionPlan))
    })

    eventSource.addEventListener('step_start', (event) => {
      const data = JSON.parse(event.data)
      console.log('Step started:', data.step_id, data.description)

      dispatch(updateMessageStepExecution(assistantMsgId, data.step_id, {
        status: 'in_progress',
        started_at: data.timestamp,
      }))
    })

    eventSource.addEventListener('step_complete', (event) => {
      const data = JSON.parse(event.data)
      console.log('Step completed:', data.step_id, data.status)

      dispatch(updateMessageStepExecution(assistantMsgId, data.step_id, {
        status: data.status,
        completed_at: data.timestamp,
      }))
    })

    eventSource.addEventListener('tool_start', (event) => {
      const data = JSON.parse(event.data)
      const currentMsg = messagesRef.current.find(m => m.id === assistantMsgId)

      // Add to active tools
      dispatch(updateMessageTools(assistantMsgId, {
        activeTools: [...(currentMsg?.activeTools || []), data.tool_name],
      }))

      // Create tool execution entry
      dispatch(addToolExecution(assistantMsgId, {
        event_id: `${data.tool_name}-${Date.now()}`,
        tool_name: data.tool_name,
        status: 'started',
        started_at: data.timestamp,
        parameters: data.parameters,
      }))
    })

    eventSource.addEventListener('tool_progress', () => {
      // Tool progress tracking (optional)
    })

    eventSource.addEventListener('tool_complete', (event) => {
      const data = JSON.parse(event.data)
      const currentMsg = messagesRef.current.find(m => m.id === assistantMsgId)

      // Update the tool execution with result
      const updatedExecutions = (currentMsg?.tool_executions || []).map(exec =>
        exec.tool_name === data.tool_name && exec.status === 'started'
          ? {
              ...exec,
              status: data.success ? 'completed' : 'failed',
              completed_at: data.timestamp,
              execution_time_ms: data.execution_time_ms,
              result: data.result,  // ⭐ Store the result from backend!
              success: data.success,
            }
          : exec
      )

      // Update active/completed tools AND tool executions in one dispatch
      dispatch(updateMessageTools(assistantMsgId, {
        activeTools: (currentMsg?.activeTools || []).filter(t => t !== data.tool_name),
        completedTools: [...(currentMsg?.completedTools || []), data.tool_name],
        tool_executions: updatedExecutions,
      }))
    })

    eventSource.addEventListener('tool_error', (event) => {
      const data = JSON.parse(event.data)
      console.error('Tool error:', data.error)
      const currentMsg = messages.find(m => m.id === assistantMsgId)
      dispatch(updateMessageTools(assistantMsgId, {
        activeTools: (currentMsg?.activeTools || []).filter(t => t !== data.tool_name),
      }))
    })

    eventSource.addEventListener('task_update', (event) => {
      const data = JSON.parse(event.data)
      updateMessage({ tasks: data.tasks || [] })
    })

    eventSource.addEventListener('thought', (event) => {
      const data = JSON.parse(event.data)
      updateMessage({ currentThought: data.thought })
    })

    eventSource.addEventListener('sources', (event) => {
      const data = JSON.parse(event.data)
      dispatch(updateMessageSources(assistantMsgId, data.sources))
    })

    eventSource.addEventListener('artifact_created', (event) => {
      const data = JSON.parse(event.data)
      accumulatedArtifacts.push({
        type: data.artifact_type,
        title: data.artifact_title,
        content: data.artifact_content,
      })
      dispatch(updateMessageArtifacts(assistantMsgId, [...accumulatedArtifacts]))
    })

    eventSource.addEventListener('content_chunk', (event) => {
      const data = JSON.parse(event.data)
      accumulatedContent += data.content
      updateMessage({ content: accumulatedContent })
    })

    eventSource.addEventListener('final_answer', (event) => {
      const data = JSON.parse(event.data)
      updateMessage({
        content: data.answer,
        is_streaming: false,
      })

      // Dispatch metadata separately
      if (data.metadata) {
        dispatch(updateMessageMetadata(assistantMsgId, data.metadata))
      }

      // Auto-open artifact preview if artifacts were created
      if (accumulatedArtifacts.length > 0 && onPreviewChange) {
        onPreviewChange(accumulatedArtifacts[0].content, accumulatedArtifacts[0].title, accumulatedArtifacts[0].type)
      }

      // Close connection after final answer
      eventSource.close()
      eventSourceRef.current = null
      dispatch(setMessageLoading(false))
      dispatch(setStreamingMessageId(null))

      // Refresh sidebar on first message so AI-generated title appears
      if (isFirstMessage && userId) {
        dispatch(fetchConversations(userId, 10, 0, { silent: true }))
      }
    })

    eventSource.addEventListener('error', (event) => {
      if (event.data) {
        try {
          const data = JSON.parse(event.data)
          enqueueSnackbar(data.error || t('error_occurred'), { variant: 'error' })
        } catch (e) {
          console.error('Failed to parse error event:', e)
        }
      }
      dispatch(setMessageError(assistantMsgId, t('error_occurred')))
    })

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error)
      eventSource.close()
      eventSourceRef.current = null
      dispatch(setMessageLoading(false))
      dispatch(setStreamingMessageId(null))
      updateMessage({ is_streaming: false })
    }

    eventSource.addEventListener('title_update', (event) => {
      const data = JSON.parse(event.data)
      dispatch(updateConversationTitleInStore(data.conversation_id, data.title))
    })

    eventSource.addEventListener('heartbeat', () => {
      // Keep connection alive
    })
  }

  const handleStop = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    dispatch(setMessageLoading(false))
    dispatch(setStreamingMessageId(null))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        bgcolor: 'background.default',
      }}
    >
      {/* Loading State */}
      {isFetching && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            {t('loading')}
          </Typography>
        </Box>
      )}

      {/* Messages List */}
      {!isFetching && messages.length > 0 && (
        <Box
          ref={messagesScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 1, sm: 2 },
            pt: { xs: 3, sm: 2 },
            pb: { xs: 1, sm: 2 },
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box sx={{ maxWidth: '850px', width: '100%', mx: 'auto' }}>
            <MessagesList
              currentLang={currentLang}
              onPreview={handleOpenPreview}
              previewContent={previewContent}
            />
            <div ref={messagesEndRef} />
          </Box>
        </Box>
      )}

      {/* Message Input Area */}
      {!isFetching && (
        <Box
          sx={{
            flexGrow: showEmptyState ? 1 : 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: showEmptyState ? 'center' : 'flex-end',
            alignItems: 'center',
            p: { xs: 1, sm: 2 },
            pb: { xs: 'calc(8px + env(safe-area-inset-bottom, 0px))', sm: 2 },
            transition: prefersReducedMotion
              ? 'none'
              : 'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Empty State Header */}
          <Fade in={showEmptyState} timeout={prefersReducedMotion ? 0 : 300}>
            <Box
              sx={{
                display: showEmptyState ? 'flex' : 'none',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 3 },
              }}
            >
              <img
                src={theme.palette.mode === 'dark'
                  ? '/logo/logo-circle-dark-mode.svg'
                  : '/logo/logo-circle.svg'
                }
                alt="Lawis Logo"
                style={{ width: '60px', height: '60px' }}
              />
              <Typography
                sx={{
                  fontSize: { xs: '22px', sm: '28px' },
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {t('everything_ready')}
              </Typography>
            </Box>
          </Fade>

          {/* Capability Chips */}
          <Fade in={showEmptyState} timeout={prefersReducedMotion ? 0 : 400}>
            <Box sx={{ display: showEmptyState ? 'flex' : 'none', justifyContent: 'center', width: '100%' }}>
              <CapabilityChips
                onPick={({ key, prompt }) => {
                  setUserMessage(prompt)
                  if (textFocusRef?.current) {
                    const el = textFocusRef.current
                    el.focus()
                    try {
                      const len = prompt.length
                      el.setSelectionRange?.(len, len)
                    } catch (_) {}
                  }
                  if (key === 'analyze_document' && fileInputRef?.current) {
                    fileInputRef.current.click()
                  }
                }}
              />
            </Box>
          </Fade>

          {/* Input Container */}
          <Box sx={{ width: '100%', maxWidth: '850px' }}>
            <UsageIndicator feature={FEATURES.AI_CHAT} sx={{ mb: 1 }} />
            <RateLimitGate feature={FEATURES.AI_CHAT}>
              <MessageInput
                value={userMessage}
                onChange={handleTextChange}
                onSubmit={handleSubmit}
                onStop={handleStop}
                isLoading={isLoading}
                currentLang={currentLang}
                textFocusRef={textFocusRef}
                fileInputRef={fileInputRef}
              />
            </RateLimitGate>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default Chat
